import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Fade, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { ChatBubbleOutline, MoreVert } from '@mui/icons-material';
import Bluebird from 'bluebird';
import { Chatbox, useParticipants } from '@talkjs/react-components';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import PagePaper from '../../../components/Common/PagePaper';
import useActiveSite from '../../../hooks/useActiveSite';
import { getSiteContactInfo } from '../../../api/sites';
import CustomMessage from '../Components/CustomMessage';
import useActiveUser from '../../../hooks/useActiveUser';
import { getUserImage } from '../../../helpers/user';
import { Roles } from '../../../models/roles';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import routes from '../../../routes';
import {
  chatConversationModel,
  chatCurrentUserModel,
  chatReopenModel,
  chatResolvedModel,
  chatSiteStaffModel,
} from '../../../models/chat';
import '../Components/chat.css';
import useHasActiveFeatureFlag from '../../../hooks/useFeatureFlags';
import { FF_CHAT } from '../../../components/FeatureFlagWrapper';
import { getChatSession } from '../../../helpers/chatSession';
import { withChatImpersonationGuard } from '../Components/ChatImpersonationGuard';

const CustomBeforeMessages = ({ common }) => {
  const { conversation, currentUser } = common;
  const participants = useParticipants(conversation.id, 30);

  const staffMembers = participants.filter((participant) => participant.user.id !== currentUser.id);

  if (conversation?.lastMessage) {
    return null;
  }

  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.secondary' }}>
      <ChatBubbleOutline fontSize="large" />
      <Typography variant="body2">
        Have a question? Send a message and your site team will get back to you when they can.
      </Typography>
      <Typography variant="caption">{staffMembers.length} site staff members in the chat</Typography>
    </Stack>
  );
};

const ContactUsChat = () => {
  const activeSite = useActiveSite();
  const user = useActiveUser();
  const history = useHistory();
  const impersonatedUser = useSelector((state) => state.app.impersonating);
  const idTokenPayload = useSelector((state) => state.app.idTokenPayload);
  const [anchorEl, setAnchorEl] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationSnapshot, setConverationSnapshot] = useState({});
  const [dialogOpen, setDialogOpen] = useState(null);
  const hasActiveChatFF = useHasActiveFeatureFlag(FF_CHAT);
  const siteStaffMembers = [
    ...(contactInfo?.site_directors.map((sd) => ({ ...sd, roles: [Roles.SiteDirector] })) || []),
    ...(contactInfo?.site_coordinators.map((sc) => ({ ...sc, roles: [Roles.SiteCoordinator] })) || []),
  ];

  const appId = process.env.REACT_APP_TALKJS_APP_ID;
  const userId = String(user?.id);
  const session = getChatSession(userId);
  const picture = getUserImage(user, !!impersonatedUser ? null : idTokenPayload, true); // user, token, skip fallback

  const setupConversation = async () => {
    if (!siteStaffMembers.length || !currentConversationId) {
      return;
    }

    // Create and add user and conversation
    session.currentUser.set(chatCurrentUserModel({ user, picture }));

    const conversation = session.conversation(currentConversationId);

    // Create conversation with id: ${user.id}_${activeSite.id}_contact_us
    conversation.createIfNotExists(chatConversationModel(user));

    conversation.participant(userId).createIfNotExists();

    // Add  site staff to conversation (only during the first initialization)
    const snapshot = await conversation.get();

    if (snapshot?.custom?.staffMembersAdded === 'true') {
      return; // prevents re-adding staff when they were removed
    }

    siteStaffMembers.forEach((member) => {
      if (member.id === user.id) {
        return;
      }

      const memberId = String(member.id);

      session.user(memberId).createIfNotExists(chatSiteStaffModel(member));

      conversation.participant(memberId).createIfNotExists();
    });

    conversation.set({ custom: { staffMembersAdded: 'true' } });
  };

  useEffect(() => {
    if (!activeSite?.id) {
      return;
    }

    Bluebird.try(() => getSiteContactInfo(activeSite.id))
      .then((res) => res.json())
      .then((json) => setContactInfo(json.data));
  }, [activeSite?.id]);

  useEffect(() => {
    if (!user.id && !activeSite.id) {
      return;
    }

    setCurrentConversationId(`${user.id}_${activeSite.id}_contact_us`);
  }, [user, activeSite]);

  useEffect(() => {
    setupConversation();
  }, [siteStaffMembers, currentConversationId]);

  useEffect(() => {
    if (!currentConversationId) {
      return;
    }
    const conversation = session.conversation(currentConversationId);

    const subscription = conversation.subscribe((snapshot) => {
      setConverationSnapshot(snapshot);
    });

    return () => subscription.unsubscribe();
  }, [currentConversationId, session]);

  const handleMoreOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleLeaveChat = () => {
    session.conversation(currentConversationId).set(chatResolvedModel(user));

    history.push(routes.index);
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(null);
  };

  const chatTheme = useMemo(() => ({
    Message: CustomMessage,
    BeforeMessages: CustomBeforeMessages,
  }));

  if (!hasActiveChatFF) {
    return history.push(routes.index);
  }

  return (
    <PagePaper
      sx={{
        height: `calc(100dvh - 80px - var(--safe-area-inset-bottom, 0px) - var(--safe-area-inset-top, 0px) ${
          Capacitor.isNativePlatform() ? '- 32px' : ''
        })`,
        paddingBottom: 'var(--safe-area-inset-bottom, 0px)',
        mb: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Stack>
          <Typography variant="h6">Contact your site team</Typography>
          <Typography variant="caption" color="text.secondary">
            {siteStaffMembers.map((staff) => staff.firstname).join(', ')}
          </Typography>
        </Stack>
        <IconButton onClick={handleMoreOpen}>
          <MoreVert />
        </IconButton>
        <Menu
          id="more-menu"
          anchorEl={anchorEl}
          TransitionComponent={Fade}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={!!anchorEl}
          onClose={handleMoreClose}
        >
          <MenuItem onClick={handleOpenDialog} sx={{ color: 'cancel.main' }}>
            Leave chat
          </MenuItem>
        </Menu>
      </Box>
      <Divider sx={{ py: 1 }} />
      <Chatbox
        style={{
          width: '100%',
          borderRadius: 0,
          border: 0,
          flex: 1,
          minHeight: 0,
        }}
        appId={appId}
        userId={userId}
        conversationId={currentConversationId}
        theme={chatTheme}
        chatHeaderVisible={false}
        onSendMessage={(event) => {
          // set resolved as false when initating user sends message
          if (conversationSnapshot?.custom?.resolved === 'true') {
            session.conversation(event.conversation.id).set(chatReopenModel(user));
          }
        }}
      />
      {dialogOpen && (
        <ConfirmationDialog
          isMobile
          buttons={[
            {
              variant: 'outlined',
              label: 'Stay',
              sx: { textTransform: 'none' },
            },
            {
              variant: 'contained',
              label: 'Leave chat',
              color: 'cancel',
              sx: { textTransform: 'none' },
              onClick: handleLeaveChat,
            },
          ]}
          closeDialog={handleCloseDialog}
          title="Leave this chat?"
          message="You won't get new replies. Your site team can still see the conversation, and you an start a new one anytime"
        />
      )}
    </PagePaper>
  );
};

export default withChatImpersonationGuard(ContactUsChat);
